import { Link } from 'react-router-dom';
import SeoHead from './SeoHead';
import { Breadcrumbs } from './ComponentLibrary';
import { pageMeta } from '../seoUtils';

function ListingPolicyPage() {
  const seo = pageMeta({
    title: 'Listing Policy',
    description:
      'The requirements for creating and maintaining property advertisements on NaijaNest.',
    path: '/listing-policy',
  });
  return (
    <div className="static-page listing-policy-page">
      <SeoHead title={seo.fullTitle} description={seo.desc} url={seo.url} image={seo.img} />
      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          { label: 'Listing Policy', current: true },
        ]}
      />
      <section className="section">
        <header className="listing-policy-page__intro">
          <h1>Listing Policy</h1>
          <p className="static-page__lead">
            Effective date: 2 August 2026. Last updated: 2 August 2026.
          </p>
        </header>

        <h2>Overview</h2>
        <p>
          NaijaNest is a digital property marketplace that allows property owners, developers,
          agents, landlords and authorised advertisers to publish property listings.
        </p>
        <p>
          This Listing Policy explains the requirements for creating and maintaining property
          advertisements on NaijaNest.
        </p>
        <p>
          By submitting a listing, you agree to comply with this policy and our{' '}
          <Link to="/terms">Terms of Service</Link>.
        </p>

        <h2>Listing Requirements</h2>
        <p>All listings must:</p>
        <ul>
          <li>contain accurate and truthful information;</li>
          <li>represent a genuine property opportunity;</li>
          <li>include correct property details;</li>
          <li>use images that accurately represent the property;</li>
          <li>provide realistic pricing information;</li>
          <li>identify the advertiser appropriately;</li>
          <li>comply with applicable laws and regulations.</li>
        </ul>
        <p>
          Advertisers are responsible for ensuring they have the necessary authority to advertise
          a property.
        </p>

        <h2>Accurate Property Information</h2>
        <p>Listings should accurately describe:</p>
        <ul>
          <li>property type;</li>
          <li>location;</li>
          <li>price;</li>
          <li>availability;</li>
          <li>features and facilities;</li>
          <li>condition of the property;</li>
          <li>relevant documentation where applicable.</li>
        </ul>
        <p>Advertisers must not intentionally provide misleading, incomplete, or false information.</p>

        <h2>Property Images</h2>
        <p>Advertisers must only upload images they have the right to use.</p>
        <p>Images should:</p>
        <ul>
          <li>represent the actual property;</li>
          <li>not intentionally hide important conditions or defects;</li>
          <li>not be copied from another person&rsquo;s listing without permission.</li>
        </ul>
        <p>
          NaijaNest may remove images or listings that appear misleading or unauthorised.
        </p>

        <h2>Duplicate Listings</h2>
        <p>
          To maintain marketplace quality, advertisers should avoid creating duplicate listings
          for the same property.
        </p>
        <p>NaijaNest may identify potential duplicate listings using information such as:</p>
        <ul>
          <li>property details;</li>
          <li>location information;</li>
          <li>images;</li>
          <li>advertiser information;</li>
          <li>listing history.</li>
        </ul>
        <p>
          A duplicate warning does not necessarily mean a listing is fraudulent. The same
          property may legitimately be advertised by different authorised parties.
        </p>
        <p>
          NaijaNest may review suspicious duplicate activity and take appropriate action where
          necessary.
        </p>

        <h2>Prohibited Listings</h2>
        <p>Listings are not permitted if they:</p>
        <ul>
          <li>advertise properties without appropriate authority;</li>
          <li>contain false or misleading information;</li>
          <li>impersonate another person or organisation;</li>
          <li>contain fraudulent claims;</li>
          <li>use stolen images or content;</li>
          <li>promote illegal activities;</li>
          <li>contain discriminatory or prohibited content;</li>
          <li>attempt to manipulate platform rankings;</li>
          <li>include spam or irrelevant information.</li>
        </ul>

        <h2>Listing Moderation</h2>
        <p>NaijaNest may:</p>
        <ul>
          <li>review listings;</li>
          <li>request additional information;</li>
          <li>restrict visibility;</li>
          <li>remove listings;</li>
          <li>suspend accounts;</li>
        </ul>
        <p>where there are concerns regarding accuracy, safety, compliance, or platform integrity.</p>
        <p>
          NaijaNest does not guarantee that every listing has been independently verified before
          publication.
        </p>

        <h2>Listing Expiry and Updates</h2>
        <p>Advertisers should keep listings updated.</p>
        <p>Advertisers are responsible for removing or updating listings that are:</p>
        <ul>
          <li>no longer available;</li>
          <li>incorrectly priced;</li>
          <li>no longer accurate.</li>
        </ul>
        <p>NaijaNest may archive outdated listings.</p>

        <h2>Reporting a Listing</h2>
        <p>Users can report listings they believe are:</p>
        <ul>
          <li>fraudulent;</li>
          <li>misleading;</li>
          <li>duplicated;</li>
          <li>inappropriate;</li>
          <li>unauthorised.</li>
        </ul>
        <p>
          Use the <strong>Report</strong> button on the listing itself, or contact us via our{' '}
          <Link to="/contact">Contact page</Link>. Reports should include relevant information to
          help NaijaNest investigate.
        </p>

        <h2>Advertiser Responsibility</h2>
        <p>Advertisers remain responsible for:</p>
        <ul>
          <li>the accuracy of their listings;</li>
          <li>their authority to advertise;</li>
          <li>communications with users;</li>
          <li>agreements and transactions entered into.</li>
        </ul>
        <p>
          NaijaNest acts as a marketplace facilitator and does not guarantee any property
          transaction. NaijaNest is not a party to agreements between advertisers and users.
        </p>

        <h2>Your Responsibility as a User</h2>
        <p>
          A listing being published on NaijaNest does not mean it has been independently
          verified. Before making decisions or payments based on a listing, you should
          independently verify the property, the advertiser, and their authority to advertise it,
          and carry out your own due diligence. See our <Link to="/safety">Safety Tips</Link> for
          guidance.
        </p>
      </section>
    </div>
  );
}
export default ListingPolicyPage;
